import { Injectable } from '@angular/core';
import { appConfig } from '../app.config';
import { SitesService } from './sites.service';
import { SupplierService } from './supplier.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class BlankstatusService {
  public Config: any;
  public updatedStatus: any[] = [];
  public errorMessArr: any[] = [];

  private _asyncResolve: (() => void) | null = null;
  private _asyncReject: ((reason?: any) => void) | null = null;

  constructor(readonly supplierService: SupplierService, readonly siteService: SitesService) {
    this.Config = appConfig;
  }

  readonly uploadStatusSubject = new BehaviorSubject<string>('');
  TextForCompleteUpload$ = this.uploadStatusSubject.asObservable();
  readonly uploadCompletedSubject = new BehaviorSubject<boolean>(false);
  uploadCompleted$ = this.uploadCompletedSubject.asObservable();
  readonly ExportFileSubject = new BehaviorSubject<Object[]>([]);
  ExportFile$ = this.ExportFileSubject.asObservable();

  updateText(message: string) {
    this.uploadStatusSubject.next(message);
  }

  updateCompletedStatus(status: boolean) {
    this.uploadCompletedSubject.next(status);
  }

  updateExportArray(data: any[]) {
    this.ExportFileSubject.next(data);
  }

  private startFunc() {
    this.updateText('Process started');
    this.updateCompletedStatus(true);
    this.updatedStatus = [];
    this.errorMessArr = [];
  }

  private resetAsyncState() {
    this._asyncResolve = null;
    this._asyncReject = null;
  }
  
  CompanyBlankStatusUpdate(companyId: string): void {
    this.startFunc();

    this.supplierService
      .getCompanies(`${this.Config.ApiSupplierUrl}companies/${companyId}`)
      .subscribe(
        async (companyData: any) => {
          try {
            if (!companyData) {
              throw new Error('Company not found');
            }

            this.updateText(`Fetched company: ${companyData['allianceIdentifier']}`);

            // Idempotent update: use existing companyStatusId (no value change)
            const companyUpdate = {
              companyStatusCode: companyData['companyStatusCode'],
              companyStatusId: companyData['companyStatusId']
            };

            await this.handleApiCall(
              firstValueFrom(
                this.supplierService.UpdateCompany(
                  `${this.Config.ApiSupplierUrl}companies/${companyData['rowId']}`,
                  companyUpdate
                )
              ),
              'Company idempotent update',
              companyData['allianceIdentifier'],
              companyData['companyStatusCode']
            );

            this.updateText('Company updated successfully');

            // Fetch and update site-assignments
            await this.updateSiteAssignmentsForCompany(companyId);

            this.updateText('Update process completed successfully');
            this.updateCompletedStatus(false);

            if (this._asyncResolve) {
              this._asyncResolve();
              this.resetAsyncState();
            }
          } catch (error) {
            console.error('Error in CompanyBlankStatusUpdate:', error);
            this.updateText('Error occurred during update process');
            this.updateCompletedStatus(false);

            if (this._asyncReject) {
              this._asyncReject(error);
              this.resetAsyncState();
            }
          }
        },
        (error) => {
          console.error('Error fetching company:', error);
          this.updateText('Error fetching company data');
          this.updateCompletedStatus(false);

          if (this._asyncReject) {
            this._asyncReject(error);
            this.resetAsyncState();
          }
        }
      );
  }

  /**
   * Helper: Update site-assignments for a company where validityEndDate is null
   */
  private async updateSiteAssignmentsForCompany(companyId: string): Promise<void> {
    try {
      let page = 1;
      const allAssignments: any[] = [];

      while (true) {
        const siteAssignments = await firstValueFrom(
          this.supplierService.getSiteAssignments(
            `${this.Config.ApiSupplierUrl}site-assignments?businessEntityCompanyId=${companyId}&status=in('VALIDATED','PENDING_VALIDATION')&_page=${page}`
          )
        );

        if (siteAssignments['site-assignments'] && siteAssignments['site-assignments'].length > 0) {
          allAssignments.push(...siteAssignments['site-assignments']);
        }

        if (siteAssignments['maxPage'] > page) {
          page++;
        } else {
          break;
        }
      }

      if (allAssignments.length === 0) {
        this.updateText('No active site assignments found for company');
        return;
      }

      for (const siteAssignment of allAssignments) {
        const update = { status: 'VALIDATED' };
        await this.handleApiCall(
          firstValueFrom(
            this.supplierService.updateSiteAssignments(
              `${this.Config.ApiSupplierUrl}site-assignments/${siteAssignment['rowId']}`,
              update
            )
          ),
          'Site assignment idempotent update',
          siteAssignment['identifier'],
          'VALIDATED'
        );
      }

      this.updateText('Site assignments updated successfully');
    } catch (error) {
      console.error('Error updating site assignments for company:', error);
      throw error;
    }
  }

  /**
   * siteBlankStatusUpdate: Perform idempotent updates for a site
   * 1. Fetch site
   * 2. Update site with existing activeStatus (no change)
   * 3. Fetch site-assignments where validityEndDate is null
   * 4. Update site-assignment with status = VALIDATED
   * 5. Fetch all access points
   * 6. Update each access point with existing activeStatus
   */
  siteBlankStatusUpdate(siteId: string): void {
    this.startFunc();

    this.siteService.getSites(`${this.Config.ApiSitesUrl}sites/${siteId}`).subscribe(
      async (siteData: any) => {
        try {
          if (!siteData) {
            throw new Error('Site not found');
          }

          this.updateText(`Fetched site: ${siteData['identifier']}`);

          // Idempotent update: use existing activeStatus (no value change)
          const siteUpdate = { activeStatus: siteData['activeStatus'] };
          await this.handleApiCall(
            firstValueFrom(
              this.siteService.updateSite(
                `${this.Config.ApiSitesUrl}sites/${siteData['rowId']}`,
                siteUpdate
              )
            ),
            'Site idempotent update',
            siteData['identifier'],
            siteData['activeStatus']
          );

          this.updateText('Site updated successfully');

          // Fetch and update site-assignments
          await this.updateSiteAssignmentsForSite(siteData['identifier']);

          // Fetch and update access points
          await this.updateAccessPointsForSite(siteData['identifier']);

          this.updateText('Update process completed successfully');
          this.updateCompletedStatus(false);

          if (this._asyncResolve) {
            this._asyncResolve();
            this.resetAsyncState();
          }
        } catch (error) {
          console.error('Error in siteBlankStatusUpdate:', error);
          this.updateText('Error occurred during update process');
          this.updateCompletedStatus(false);

          if (this._asyncReject) {
            this._asyncReject(error);
            this.resetAsyncState();
          }
        }
      },
      (error) => {
        console.error('Error fetching site:', error);
        this.updateText('Error fetching site data');
        this.updateCompletedStatus(false);

        if (this._asyncReject) {
          this._asyncReject(error);
          this.resetAsyncState();
        }
      }
    );
  }

  /**
   * Helper: Update site-assignments for a site where validityEndDate is null
   */
  private async updateSiteAssignmentsForSite(siteIdentifier: string): Promise<void> {
    try {
      let page = 1;
      const allAssignments: any[] = [];

      while (true) {
        const siteAssignments = await firstValueFrom(
          this.supplierService.getSiteAssignments(
            `${this.Config.ApiSupplierUrl}site-assignments?reaIdentifier=${siteIdentifier}&status=in('VALIDATED','PENDING_VALIDATION')&_page=${page}`
          )
        );

        if (siteAssignments['site-assignments'] && siteAssignments['site-assignments'].length > 0) {
          allAssignments.push(...siteAssignments['site-assignments']);
        }

        if (siteAssignments['maxPage'] > page) {
          page++;
        } else {
          break;
        }
      }

      if (allAssignments.length === 0) {
        this.updateText('No active site assignments found for site');
        return;
      }

      for (const siteAssignment of allAssignments) {
        const update = { status: 'VALIDATED' };
        await this.handleApiCall(
          firstValueFrom(
            this.supplierService.updateSiteAssignments(
              `${this.Config.ApiSupplierUrl}site-assignments/${siteAssignment['rowId']}`,
              update
            )
          ),
          'Site assignment idempotent update',
          siteAssignment['identifier'],
          'VALIDATED'
        );
      }

      this.updateText('Site assignments updated successfully');
    } catch (error) {
      console.error('Error updating site assignments:', error);
      throw error;
    }
  }

  /**
   * Helper: Update access points for a site with existing activeStatus
   */
  private async updateAccessPointsForSite(siteIdentifier: string): Promise<void> {
    try {
      let page = 1;
      const allAccessPoints: any[] = [];

      while (true) {
        const accessPointsData = await firstValueFrom(
          this.siteService.getAccessPoints(
            `${this.Config.ApiSitesUrl}access-points?siteIdentifier=${siteIdentifier}&_page=${page}`
          )
        );

        if (accessPointsData['access-points'] && accessPointsData['access-points'].length > 0) {
          allAccessPoints.push(...accessPointsData['access-points']);
        }

        if (accessPointsData['maxPage'] > page) {
          page++;
        } else {
          break;
        }
      }

      if (allAccessPoints.length === 0) {
        this.updateText('No access points found for site');
        return;
      }

      for (const accessPoint of allAccessPoints) {
        // Only update active access points
        // if (accessPoint['activeStatus'] === 'ACTIVE') {
          const update = { activeStatus: accessPoint['activeStatus'] };
          await this.handleApiCall(
            firstValueFrom(
              this.siteService.updateAccessPoint(
                `${this.Config.ApiSitesUrl}access-points/${accessPoint['rowId']}`,
                update
              )
            ),
            'Access point idempotent update',
            accessPoint['identifier'],
            accessPoint['activeStatus']
          );
        // }
      }

      this.updateText('Access points updated successfully');
    } catch (error) {
      console.error('Error updating access points:', error);
      throw error;
    }
  }

  /**
   * Handle API calls with error tracking and status reporting
   */
  private async handleApiCall(
    apiCall: Promise<any>,
    operation: string,
    identifier: string,
    statusApplied: string = ''
  ): Promise<void> {
    try {
      this.errorMessArr.push({ error: operation, identifier });
      let existing = this.updatedStatus.find((item: any) => item.identifier === identifier);

      if (!existing) {
        existing = { identifier, updated: '', reverted: '' };
        this.updatedStatus.push(existing);
      }

      existing.updated = 'yes';
      existing.reverted = statusApplied;
      this.updateExportArray(this.updatedStatus);

      await apiCall;
    } catch (error) {
      console.error(`${operation} failed for ${identifier}:`, error);
      let existing = this.updatedStatus.find((item: any) => item.identifier === identifier);

      if (!existing) {
        existing = { identifier, updated: '', reverted: '' };
        this.updatedStatus.push(existing);
      }

      existing.updated = 'no';
      this.updateExportArray(this.updatedStatus);

      throw error;
    }
  }

  /**
   * Promise-based wrapper for async site operations
   */
  siteBlankStatusUpdateAsync(siteId: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this._asyncResolve = resolve;
      this._asyncReject = reject;

      try {
        this.siteBlankStatusUpdate(siteId);
      } catch (error) {
        reject(error);
        this.resetAsyncState();
      }
    });
  }

  /**
   * Promise-based wrapper for async company operations
   */
  CompanyBlankStatusUpdateAsync(companyId: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this._asyncResolve = resolve;
      this._asyncReject = reject;

      try {
        this.CompanyBlankStatusUpdate(companyId);
      } catch (error) {
        reject(error);
        this.resetAsyncState();
      }
    });
  }
}
